import { ONE_CLICK_MV_V2_MAX_IMAGE_COUNT, TTS_EMOTIONS, toolTaskSupportsTextPrompt } from '@/app/constants';
import type { FormImagePrompt, TaskInputPrompts, TaskParams, ToolFormParams } from '@/app/types';
import { isHevcSupported } from './device-utils';

const buildMaterialTaskInputPromptFromFormImage = (
  ref: FormImagePrompt,
  fallbackName: string,
): TaskInputPrompts | null => {
  if (!ref.material?.id) {
    return null;
  }

  const content = ref.prompt?.selectedImage || ref.prompt?.sourceImage || '';

  return {
    type: 'material',
    content,
    name: ref.material.name || fallbackName,
    material: {
      id: ref.material.id,
      version: ref.material.version,
      share_id: ref.material.share_id,
    },
  };
};

type TTSPrompt = {
  type: 'text';
  content: string;
  audio?: {
    emotion?: string;
    pitch: number;
  };
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const TTS_EMOTION_PROMPT_REGEX = new RegExp(
  `\\{(${TTS_EMOTIONS.map(({ id }) => escapeRegExp(id)).join('|')})\\}([\\s\\S]*?)\\{/\\1\\}`,
  'g',
);

const pushTTSPrompt = (prompts: TTSPrompt[], prompt: TTSPrompt) => {
  if (!prompt.content) return;
  prompts.push(prompt);
};

type OneClickMvApiMode = 'unspecified' | 'sing' | 'sing_perform' | 'perform' | 'dance';
type OneClickMvApiSettings = {
  mode?: OneClickMvApiMode;
  subtitle_enable?: boolean;
  subtitle_color?: string;
};

const ONE_CLICK_MV_MODE_MAP: Record<Exclude<ToolFormParams['oneClickMvScene'], undefined>, OneClickMvApiMode> = {
  sing: 'sing',
  sing_act: 'sing_perform',
  dance: 'dance',
  performance: 'perform',
};

type OneClickMvRolePrompt = TaskInputPrompts & {
  lip_sync: {
    enable: boolean;
    index: number;
  };
};

type UsableImagePrompt = FormImagePrompt & {
  status: 'done';
  prompt: NonNullable<FormImagePrompt['prompt']>;
};

const hasUsableImagePrompt = (ref: FormImagePrompt): ref is UsableImagePrompt => {
  return ref.status === 'done' && !!ref.prompt?.selectedImage && !!ref.prompt?.sourceImage;
};

type UsableAudioPrompt = ToolFormParams['audioPrompts'][number] & {
  status: 'done';
  prompt: NonNullable<ToolFormParams['audioPrompts'][number]['prompt']>;
};

const hasUsableAudioPrompt = (ref: ToolFormParams['audioPrompts'][number]): ref is UsableAudioPrompt => {
  return ref.status === 'done' && !!ref.prompt?.src;
};

const convertTextPromptToTTSPrompts = ({ textPrompt, pitch }: { textPrompt?: string; pitch: number }): TTSPrompt[] => {
  const rawText = textPrompt || '';
  if (!rawText) return [];

  const prompts: TTSPrompt[] = [];
  let lastIndex = 0;

  for (const match of rawText.matchAll(TTS_EMOTION_PROMPT_REGEX)) {
    const [matchedText, emotion, content] = match;
    const matchIndex = match.index ?? 0;

    pushTTSPrompt(prompts, {
      type: 'text',
      content: rawText.slice(lastIndex, matchIndex),
      audio: {
        pitch,
      },
    });

    pushTTSPrompt(prompts, {
      type: 'text',
      content,
      audio: {
        emotion,
        pitch,
      },
    });

    lastIndex = matchIndex + matchedText.length;
  }

  pushTTSPrompt(prompts, {
    type: 'text',
    content: rawText.slice(lastIndex),
    audio: {
      pitch,
    },
  });

  return prompts;
};

export const convertTTSPromptsToTextPrompt = (prompts?: TaskInputPrompts[]): string => {
  return (prompts || [])
    .filter((prompt): prompt is TTSPrompt => prompt.type === 'text')
    .map((prompt) => {
      const emotion = prompt.audio?.emotion;
      if (!emotion) {
        return prompt.content;
      }

      return `{${emotion}}${prompt.content}{/${emotion}}`;
    })
    .join('');
};

export const buildTTSRecreatePatch = ({
  input,
  settings,
}: {
  input: TaskParams['input'];
  settings?: TaskParams['settings'];
}): Pick<ToolFormParams, 'settings' | 'textPrompt'> => ({
  settings: {
    ...settings,
    pitch: input.prompts?.find((prompt) => prompt.type === 'text')?.audio?.pitch || 0,
  } as ToolFormParams['settings'],
  textPrompt: convertTTSPromptsToTextPrompt(input.prompts),
});

export const convertToolTask = ({
  taskType,
  formParams,
}: {
  taskType: TaskParams['type'];
  formParams: ToolFormParams;
}): TaskParams => {
  // Handle text2tts separately
  if (taskType === 'tts') {
    const ttsSettings = formParams.settings as ToolFormParams['settings'] & { pitch?: number };
    const prompts = convertTextPromptToTTSPrompts({
      textPrompt: formParams.textPrompt,
      pitch: typeof ttsSettings.pitch === 'number' ? ttsSettings.pitch : 0,
    });
    const plainText = prompts.map((prompt) => prompt.content).join('');

    return {
      input: {
        prompts,
        enhance: true,
      },
      settings: {
        ...formParams.settings,
      },
      type: taskType,
      text: plainText,
      ...(formParams.extraParams ? { extra_params: formParams.extraParams } : {}),
    };
  }

  const isOneClickMvV2 = taskType === 'music_video' && formParams.oneClickMvVersion === 'v2';
  const isOneClickMvLipSyncScene =
    isOneClickMvV2 && (formParams.oneClickMvScene === 'sing' || formParams.oneClickMvScene === 'sing_act');
  const existingMusicVideoSettings = (
    formParams.settings as TaskParams['settings'] & {
      music_video?: OneClickMvApiSettings;
    }
  ).music_video;
  const musicVideoSettings =
    taskType === 'music_video'
      ? isOneClickMvV2
        ? {
            ...existingMusicVideoSettings,
            mode: ONE_CLICK_MV_MODE_MAP[formParams.oneClickMvScene || 'sing'],
            subtitle_enable: formParams.oneClickMvSubtitleEnabled ?? false,
            ...(formParams.oneClickMvSubtitleEnabled ? { subtitle_color: '#FFFFFF' } : {}),
          }
        : {
            ...existingMusicVideoSettings,
            mode: 'unspecified' as const,
          }
      : undefined;
  const uploadedAudioDuration = formParams.audioPrompts.find(hasUsableAudioPrompt)?.prompt.duration;
  const settings = isOneClickMvV2
    ? ({
        ...formParams.settings,
        music_video: musicVideoSettings,
        ...(typeof uploadedAudioDuration === 'number' ? { duration: Math.round(uploadedAudioDuration) } : {}),
      } as TaskParams['settings'] & {
        music_video: {
          mode: OneClickMvApiMode;
        };
      })
    : taskType === 'music_video' && typeof uploadedAudioDuration === 'number'
      ? {
          ...formParams.settings,
          music_video: musicVideoSettings,
          duration: Math.round(uploadedAudioDuration),
        }
      : formParams.settings;
  const {
    imagePrompts,
    videoPrompts,
    audioPrompts,
    textPrompt,
    remixStyle,
    subtitlePrompts,
    referenceImagePrompts,
    extraParams,
  } = formParams;
  const prompts: TaskInputPrompts[] = [];

  if (toolTaskSupportsTextPrompt(taskType) && textPrompt) {
    prompts.push({ type: 'text', content: textPrompt });
  }

  if (taskType === 'style_transfer' && remixStyle) {
    prompts.push({
      type: 'remix_style',
      content: remixStyle.name || remixStyle.id,
      remix_style: {
        id: remixStyle.id,
        name: remixStyle.name || '',
        url: remixStyle.url || '',
      },
    });
  }

  videoPrompts
    .filter((p) => p.id !== formParams.creationId)
    .forEach((ref, idx) => {
      if (ref.status !== 'done' || !ref.prompt) return;
      prompts.push({
        type: 'video',
        content: ref.prompt.video,
        name: `视频${idx + 1}`,
        cover_uri: ref.prompt.coverImage,
        reference: ref.reference,
      });
    });

  if (isOneClickMvLipSyncScene) {
    referenceImagePrompts?.filter(hasUsableImagePrompt).forEach((ref, idx) => {
      prompts.push({
        type: 'image',
        content: ref.prompt.selectedImage,
        src_imgs: [ref.prompt.sourceImage],
        name: `对口型角色${idx + 1}`,
        lip_sync: {
          enable: true,
          index: idx,
        },
      } as OneClickMvRolePrompt);
    });
  }

  imagePrompts
    .filter((p) => p.id !== formParams.creationId)
    .slice(0, isOneClickMvV2 ? ONE_CLICK_MV_V2_MAX_IMAGE_COUNT : undefined)
    .forEach((ref, idx) => {
      if (isOneClickMvV2 && !hasUsableImagePrompt(ref)) return;
      if (ref.status !== 'done' || !ref.prompt) return;

      const materialPrompt = buildMaterialTaskInputPromptFromFormImage(ref, `图片${idx + 1}`);
      if (materialPrompt) {
        prompts.push(materialPrompt);
        return;
      }

      const prompt: TaskInputPrompts = {
        type: 'image',
        content: ref.prompt.selectedImage,
        src_imgs: [ref.prompt.sourceImage],
        name: `图片${idx + 1}`,
        reference: ref.reference,
      };

      if (ref.prompt.selectedImageRange) {
        prompt.selected_region = {
          top_left: { x: ref.prompt.selectedImageRange[0], y: ref.prompt.selectedImageRange[1] },
          bottom_right: { x: ref.prompt.selectedImageRange[2], y: ref.prompt.selectedImageRange[3] },
        };
      }
      prompts.push(prompt);
    });

  audioPrompts.forEach((ref, idx) => {
    if (!hasUsableAudioPrompt(ref)) return;
    prompts.push({
      type: 'audio',
      content: ref.prompt.src,
      name: `音频${idx + 1}`,
    });
  });

  if (!isOneClickMvV2) {
    subtitlePrompts?.forEach((ref) => {
      if (ref.status !== 'done' || !ref.prompt?.src) return;
      prompts.push({
        type: 'subtitle',
        content: ref.prompt.src,
      });
    });
  }

  return {
    input: {
      creation_id: !!formParams.creationId && formParams.creationId !== '0' ? formParams.creationId : undefined,
      prompts,
      enhance: true,
      ...(isOneClickMvLipSyncScene ? { lip_sync: true } : {}),
    },
    settings: {
      ...settings,
      ...(taskType && taskType !== 'upscale_image' ? { codec: isHevcSupported() ? 'h265' : 'h264' } : {}),
    },
    type: taskType,
    ...(extraParams && taskType !== 'music_video'
      ? {
          extra_params: extraParams,
        }
      : {}),
  };
};
